import Typography from "@material-ui/core/Typography";
import React from "react";
import { type SetterOrUpdater, useRecoilState, useRecoilValue } from "recoil";
import CustomSelect from "../components/CustomSelect";
import {
	atEveryOptions,
	atOptionsNonAdmin,
	defaultMinuteOptions,
	defaultMinuteOptionsWithOrdinal,
	DEFAULT_MINUTE_OPTS,
} from "../constants";
import {
	isAdminState,
	localeState,
	minuteAtEveryState,
	minuteRangeEndSchedulerState,
	minuteRangeStartSchedulerState,
	minuteState,
} from "../store";
import { Grid } from "@material-ui/core";
import type { SelectOptions } from "../types";

export default function Minute() {
	const [minuteAtEvery, setMinuteAtEvery] = useRecoilState(minuteAtEveryState);
	const [startMinute, setStartMinute] = useRecoilState(
		minuteRangeStartSchedulerState,
	);
	const [endMinute, setEndMinute] = useRecoilState(
		minuteRangeEndSchedulerState,
	);
	const [minute, setMinute] = useRecoilState(minuteState);
	const [minuteOptions, setMinuteOptions] = React.useState(DEFAULT_MINUTE_OPTS);

	const [possibleStartTimes, setPossibleStartTimes] = React.useState(
		defaultMinuteOptionsWithOrdinal(),
	);

	const [possibleEndTimes, setPossibleEndTimes] = React.useState(
		defaultMinuteOptionsWithOrdinal(),
	);

	React.useEffect(() => {
		const startIndex = possibleStartTimes.findIndex(
			(x) => x.value === startMinute.value,
		);
		const limitedPossibleTimeRange = possibleEndTimes.map(
			(possibleEndTime, index) => ({
				...possibleEndTime,
				disabled: index <= startIndex,
			}),
		);
		setPossibleEndTimes(limitedPossibleTimeRange);
	}, [startMinute]);

	React.useEffect(() => {
		const endIndex = possibleEndTimes.findIndex(
			(x) => x.value === endMinute.value,
		);
		const limitedPossibleTimeRange = possibleStartTimes.map(
			(possibleStartTime, index) => ({
				...possibleStartTime,
				disabled: index >= endIndex,
			}),
		);
		setPossibleStartTimes(limitedPossibleTimeRange);
	}, [endMinute]);

	const isAdmin = useRecoilValue(isAdminState);

	React.useEffect(() => {
		if (minuteAtEvery.value === "every") {
			if (minute.length > 1) {
				setMinute([minuteOptions[1]]);
			} else if (minute[0].value === "0") {
				setMinute([minuteOptions[1]]);
			}
			setMinuteOptions((prevMinuteOptions) =>
				prevMinuteOptions.map((prevMinuteOption) => ({
					...prevMinuteOption,
					...(prevMinuteOption.value === "0" && { disabled: true }),
				})),
			);
		} else {
			setMinuteOptions(defaultMinuteOptions);
		}
	}, [minuteAtEvery, isAdmin]);

	React.useEffect(() => {
		if (!isAdmin && minute.length > 1) {
			setMinute((prevMinute) => [prevMinute[0]]);
		}
	}, [isAdmin]);

	const resolvedLocale = useRecoilValue(localeState);

	return (
		<>
			<Grid container>
				<Grid item lg={6} md={6} sm={12} xs={12}>
					<CustomSelect
						single
						options={
							isAdmin
								? atEveryOptions(
										resolvedLocale.atOptionLabel,
										resolvedLocale.everyOptionLabel,
									)
								: atOptionsNonAdmin(
										resolvedLocale.atOptionLabel,
										resolvedLocale.everyOptionLabel,
									)
						}
						label={resolvedLocale.atEveryText}
						disableClearable
						value={minuteAtEvery}
						setValue={
							setMinuteAtEvery as SetterOrUpdater<
								SelectOptions | SelectOptions[]
							>
						}
						multiple={false}
					/>
				</Grid>

				<Grid item lg={6} md={6} sm={12} xs={12}>
					<CustomSelect
						options={minuteOptions}
						label={resolvedLocale.minuteLabel}
						value={minute}
						setValue={
							setMinute as SetterOrUpdater<SelectOptions | SelectOptions[]>
						}
						disableClearable={
							minuteAtEvery.value === "every" || minute.length < 2
						}
						single={minuteAtEvery.value === "every" || !isAdmin}
						sort
						disableEmpty
						disabled={minuteAtEvery.value === "every" && !isAdmin}
						limitTags={3}
					/>
					{minuteAtEvery.value === "every" && (
						<>
							<Grid item lg={6} md={6} sm={12} xs={12}>
								<Typography>{resolvedLocale.betweenText}</Typography>
								<CustomSelect
									single
									options={possibleStartTimes}
									label={""}
									value={startMinute}
									setValue={
										setStartMinute as SetterOrUpdater<
											SelectOptions | SelectOptions[]
										>
									}
									multiple={false}
									disableClearable
									disabled={!isAdmin}
								/>
							</Grid>
							<Grid item lg={6} md={6} sm={12} xs={12}>
								<Typography>{resolvedLocale.andText}</Typography>
								<CustomSelect
									single
									options={possibleEndTimes}
									label={""}
									value={endMinute}
									setValue={
										setEndMinute as SetterOrUpdater<
											SelectOptions | SelectOptions[]
										>
									}
									multiple={false}
									disableClearable
									disabled={!isAdmin}
								/>
							</Grid>
						</>
					)}
				</Grid>
			</Grid>
		</>
	);
}
